import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import { createUpdateERequisition, fetchERequisitionMaster, ERequisitionItem } from '../../../core/data/redux/eRequisitionSlice';

export interface OptionType {
  code: string;
  label: string;
}

const ERequisitionITCreate: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const employeesState = useSelector((state: RootState) => state.employeesState);
  const eState = useSelector((state: RootState) => state.eRequisitionState);

  const profile = employeesState?.data?.data?.details || employeesState?.data?.details;
  const empId = String(profile?.empId || profile?.employee_id || '');
  const employeeName = [profile?.firstname, profile?.lastname].filter(Boolean).join(' ') || profile?.fullName || profile?.EmployeeName || '-';
  const designation = profile?.sap_department_designation_wings?.designation_name || profile?.designation || '-';
  const department = profile?.sap_department_designation_wings?.department_name || profile?.department || '-';
  const wing = profile?.sap_department_designation_wings?.wing_name || profile?.wing || '-';

  const masterRaw = eState?.masterData?.data?.data || eState?.masterData?.data;

  // Dynamic Master List Mappers
  const getDynamicMaster = (key: string): OptionType[] => {
    const list = masterRaw?.[key];
    if (Array.isArray(list) && list.length > 0) {
      return list.map((item: any) => ({ code: String(item.Value), label: String(item.Ddtext).trim() }));
    }
    return [];
  };

  const formTypesList = getDynamicMaster('Form_Type');
  const itemOptionsList = getDynamicMaster('Item_List_Type');
  const vppStatusOptions = getDynamicMaster('VPP_Status_Type');
  const userVppOptions = getDynamicMaster('User_VPP_Type');
  const userMprOptions = getDynamicMaster('User_MPR_Type');
  const servicesOptions = getDynamicMaster('Services_Type');
  const firewallStatusOptions = getDynamicMaster('Firewall_Status_Type');
  const supervisorOptions = getDynamicMaster('Supervisor_Type');
  const itAssetOptions = getDynamicMaster('IT_Asset_Type');

  const [form, setForm] = useState({
    mobileNo: '',
    officialEmailId: '',
    officeLocation: '',
    selectRequestFromType: '',
    alreadyIssueITAsset: '1',
    supervisorUnderHOD: '1',
    workingFromDate: '',
    vppStatus: '',
    userProfileForVPP: '',
    userProfileForMPR: '',
    accessDurationFrom: '',
    accessDurationTo: '',
    macAddress: '',
    firewallStatus: '',
    services: '',
    systemIPAddress: '',
    antivirus: '',
    operatingSystemVersion: '',
    detailsOfServer: '',
    remarks: ''
  });

  const [items, setItems] = useState<ERequisitionItem[]>([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const [newItem, setNewItem] = useState({ Items: '', Remarks: '', AllotementOrNot: '1' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchERequisitionMaster({ page: 1, limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setForm(prev => ({
        ...prev,
        mobileNo: profile.phone_number || profile.mobile || prev.mobileNo,
        officialEmailId: profile.email || profile.official_email || prev.officialEmailId,
        officeLocation: profile.office_location || profile.location || prev.officeLocation
      }));
    }
  }, [profile]);

  const handleOpenItemModal = () => {
    const defaultCode = itemOptionsList[0]?.code || '1';
    setNewItem({ Items: defaultCode, Remarks: '', AllotementOrNot: '1' });
    setShowItemModal(true);
  };

  const handleSaveItemModal = () => {
    if (!newItem.Items) return toast.warning('Please select an item');
    setItems(prev => [
      ...prev,
      {
        SerialNumber: String(prev.length + 1).padStart(2, '0'),
        Items: newItem.Items,
        Remarks: newItem.Remarks.trim(),
        AllotementOrNot: newItem.AllotementOrNot || '1'
      }
    ]);
    setShowItemModal(false);
  };

  const handleRemoveItem = (index: number) => {
    setItems(prev => prev.filter((_, idx) => idx !== index).map((it, idx) => ({ ...it, SerialNumber: String(idx + 1).padStart(2, '0') })));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.mobileNo.trim() || !/^\d{10}$/.test(form.mobileNo.trim())) errs.mobileNo = '10-digit mobile number required';
    if (!form.officialEmailId.trim() || !/\S+@\S+\.\S+/.test(form.officialEmailId.trim())) errs.officialEmailId = 'Valid email required';
    if (!form.officeLocation.trim()) errs.officeLocation = 'Office location required';
    if (!form.selectRequestFromType) errs.selectRequestFromType = 'Form type selection is required';
    if (form.selectRequestFromType === '00' && items.length === 0) errs.items = 'Please add at least one equipment item';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFormSubmit = async (actionType: 'C' | 'V') => {
    if (actionType === 'C' && !validate()) return toast.error('Please fix form errors');
    setIsSubmitting(true);

    const type = form.selectRequestFromType;
    let payload: any = {
      Action: type === '14' ? 'C' : actionType,
      ReferenceNumber: '',
      EmpID: empId,
      ContactNumber: form.mobileNo.trim(),
      OfficeLocation: form.officeLocation.trim(),
      EmailID: form.officialEmailId.trim(),
      SelectRequestFromType: type
    };

    if (type === '00') {
      payload.AlreadyIssueITAsset = form.alreadyIssueITAsset;
      payload.SupervisorUnderHOD = form.supervisorUnderHOD;
      payload.List_Item = {
        results: items.map(it => ({
          ReferenceNumber: '',
          SerialNumber: it.SerialNumber,
          Items: it.Items,
          Remarks: it.Remarks,
          AllotementOrNot: it.AllotementOrNot || '1'
        }))
      };
    } else if (type === '11') {
      payload.WorkingFromDate = form.workingFromDate || '';
    } else if (type === '12') {
      payload.VPPStatus = form.vppStatus;
      payload.UserProfileForVPP = form.userProfileForVPP;
      payload.UserProfileForMPR = form.userProfileForMPR;
    } else if (type === '13') {
      payload.OperatingSystemVersion = form.operatingSystemVersion;
      payload.Antivirus = form.antivirus;
      payload.AccessDurationFrom = form.accessDurationFrom;
      payload.AccessDurationTo = form.accessDurationTo;
      payload.MacAddress = form.macAddress;
      payload.FirewallStatus = form.firewallStatus;
      payload.DetailsOfServer = form.detailsOfServer;
    } else if (type === '14') {
      payload.Services = form.services;
      payload.SystemIPAddress = form.systemIPAddress;
      payload.ORemarks = form.remarks;
    }

    try {
      const res = await dispatch(createUpdateERequisition(payload));
      if (createUpdateERequisition.fulfilled.match(res)) {
        toast.success(actionType === 'V' ? 'Saved & validated!' : 'Request submitted successfully!');
        navigate(routes.eRequisitionIT.list);
      } else toast.error((res.payload as string) || 'Submission failed');
    } catch (err: any) { toast.error(err?.message || 'Submission error'); }
    finally { setIsSubmitting(false); }
  };

  const selectedTypeObj = formTypesList.find(t => t.code === form.selectRequestFromType);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-0 text-dark fw-bold">E-Requisition (IT) - New Request</h3>
            <nav><ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><Link to={routes.employeeDashboard || '#'}>Dashboard</Link></li>
              <li className="breadcrumb-item"><Link to={routes.eRequisitionIT.list}>E-Requisition (IT)</Link></li>
              <li className="breadcrumb-item active">Create</li>
            </ol></nav>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0 fw-bold text-dark"><i className="ti ti-file-description me-2 text-danger"></i>Application Form</h5>
          </div>
          <div className="card-body">
            <form onSubmit={e => e.preventDefault()}>
              <div className="row g-3">
                {/* Employee Readonly Profile Fields */}
                {[{ l: 'Employee Name', v: employeeName }, { l: 'Employee No.', v: empId }, { l: 'Designation', v: designation }, { l: 'Department', v: department }, { l: 'Wing', v: wing }].map((f, i) => (
                  <div className="col-md-6" key={i}><label className="form-label text-dark fw-semibold small">{f.l}</label><input type="text" className="form-control bg-light text-muted" value={f.v} readOnly /></div>
                ))}

                {/* Shared Contact Info */}
                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold small">Mobile No. <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control ${errors.mobileNo ? 'is-invalid' : ''}`} value={form.mobileNo} onChange={e => setForm({ ...form, mobileNo: e.target.value })} maxLength={10} placeholder="10-digit mobile" />
                  {errors.mobileNo && <div className="invalid-feedback">{errors.mobileNo}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold small">Official Email <span className="text-danger">*</span></label>
                  <input type="email" className={`form-control ${errors.officialEmailId ? 'is-invalid' : ''}`} value={form.officialEmailId} onChange={e => setForm({ ...form, officialEmailId: e.target.value })} placeholder="Official email" />
                  {errors.officialEmailId && <div className="invalid-feedback">{errors.officialEmailId}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold small">Office Location <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control ${errors.officeLocation ? 'is-invalid' : ''}`} value={form.officeLocation} onChange={e => setForm({ ...form, officeLocation: e.target.value })} placeholder="Office location" />
                  {errors.officeLocation && <div className="invalid-feedback">{errors.officeLocation}</div>}
                </div>

                {/* Form Type Trigger (Empty by default) */}
                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold small">Select request form type <span className="text-danger">*</span></label>
                  <div
                    className={`form-select cursor-pointer bg-white d-flex align-items-center justify-content-between py-2 ${errors.selectRequestFromType ? 'is-invalid' : ''}`}
                    onClick={() => setShowTypeModal(true)}
                  >
                    <span className={selectedTypeObj ? 'text-dark fw-medium' : 'text-muted'}>
                      {selectedTypeObj ? `${selectedTypeObj.code} - ${selectedTypeObj.label}` : 'Select Request Form Type...'}
                    </span>
                  </div>
                  {errors.selectRequestFromType && <div className="invalid-feedback d-block">{errors.selectRequestFromType}</div>}
                </div>

                {/* ========================================================================= */}
                {/* DYNAMIC FORM FIELDS (Rendered only after Form Type selection)             */}
                {/* ========================================================================= */}

                {/* Form Type "00": Application for issuance of new IT equipment */}
                {form.selectRequestFromType === '00' && (
                  <>
                    <div className="col-12 mt-4">
                      <div className="card border rounded-3 shadow-xs overflow-hidden">
                        <div className="card-header bg-light border-bottom d-flex align-items-center justify-content-between py-3">
                          <div className="d-flex align-items-center gap-2">
                            <h6 className="mb-0 fw-bold text-dark"><i className="ti ti-devices me-2 text-danger fs-18"></i>Equipment Requisition Items</h6>
                            {items.length > 0 && <span className="badge bg-danger-subtle text-danger rounded-pill px-2 py-1 fs-12">{items.length} {items.length === 1 ? 'Item' : 'Items'}</span>}
                          </div>
                          {items.length > 0 && (
                            <button type="button" className="btn btn-sm btn-danger shadow-xs" onClick={handleOpenItemModal}>
                              <i className="ti ti-plus me-1"></i>Add Item
                            </button>
                          )}
                        </div>

                        {items.length === 0 ? (
                          <div className="card-body p-4 text-center bg-light-subtle">
                            <div className="avatar avatar-xl bg-danger-subtle text-danger rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                              <i className="ti ti-devices-pc fs-24"></i>
                            </div>
                            <h6 className="fw-bold text-dark mb-1">No Equipment Items Added Yet</h6>
                            <p className="text-muted small mb-3">Add hardware or peripherals required for this IT requisition.</p>
                            <button type="button" className="btn btn-danger btn-sm px-4 shadow-sm" onClick={handleOpenItemModal}>
                              <i className="ti ti-plus me-1"></i> Add Equipment Item
                            </button>
                            {errors.items && <div className="text-danger small mt-2 fw-semibold">{errors.items}</div>}
                          </div>
                        ) : (
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-hover align-middle mb-0">
                                <thead className="table-light border-bottom">
                                  <tr>
                                    <th className="text-center py-3" style={{ width: '60px' }}>Sr</th>
                                    <th className="py-3" style={{ minWidth: '220px' }}>Requested Item</th>
                                    <th className="py-3">Remarks / Specification</th>
                                    <th className="py-3 text-center" style={{ width: '160px' }}>Allotment Required</th>
                                    <th className="text-center py-3" style={{ width: '80px' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((item, idx) => {
                                    const opt = itemOptionsList.find(o => o.code === item.Items);
                                    return (
                                      <tr key={idx}>
                                        <td className="fw-bold text-center text-secondary">{item.SerialNumber}</td>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <span className="badge bg-danger-subtle text-danger me-2 px-2 py-1 fs-12">{item.Items}</span>
                                            <span className="fw-semibold text-dark">{opt?.label || item.Items}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <span className="text-secondary small">{item.Remarks || '-'}</span>
                                        </td>
                                        <td className="text-center">
                                          {item.AllotementOrNot === '1' ? (
                                            <span className="badge bg-success-subtle text-success px-2 py-1"><i className="ti ti-check me-1"></i>Yes (Allot)</span>
                                          ) : (
                                            <span className="badge bg-secondary-subtle text-secondary px-2 py-1"><i className="ti ti-x me-1"></i>No (Check Only)</span>
                                          )}
                                        </td>
                                        <td className="text-center">
                                          <button type="button" className="btn btn-icon btn-sm btn-outline-danger border-0 rounded-circle" title="Remove Item" onClick={() => handleRemoveItem(idx)}>
                                            <i className="ti ti-trash fs-16"></i>
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mt-3">
                      <label className="form-label text-dark fw-semibold small">Only for requirement of Supervisors: PCs issued to Supervisors are not more than 50% of Supervisors working under concerned HOD</label>
                      <select className="form-select" value={form.supervisorUnderHOD} onChange={e => setForm({ ...form, supervisorUnderHOD: e.target.value })}>
                        {supervisorOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>

                    <div className="col-md-6 mt-3">
                      <label className="form-label text-dark fw-semibold small">Already Issued IT assets (if any)</label>
                      <select className="form-select" value={form.alreadyIssueITAsset} onChange={e => setForm({ ...form, alreadyIssueITAsset: e.target.value })}>
                        {itAssetOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>
                  </>
                )}

                {/* Form Type "11": Application for creation of e-office ID */}
                {form.selectRequestFromType === '11' && (
                  <>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Date of Birth</label>
                      <input type="text" className="form-control bg-light text-muted" value={profile?.birthdate || ''} readOnly />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Type of Appointment</label>
                      <input type="text" className="form-control bg-light text-muted" value={profile?.employmentType || ''} readOnly />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">From date working in current department <span className="text-danger">*</span></label>
                      <input type="date" className="form-control" value={form.workingFromDate} onChange={e => setForm({ ...form, workingFromDate: e.target.value })} />
                    </div>
                  </>
                )}

                {/* Form Type "12": Application for creation of VPP/MPR portal ID */}
                {form.selectRequestFromType === '12' && (
                  <>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Status</label>
                      <select className="form-select" value={form.vppStatus} onChange={e => setForm({ ...form, vppStatus: e.target.value })}>
                        <option value="">Select Status</option>
                        {vppStatusOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Select if you want authorization for VPP</label>
                      <select className="form-select" value={form.userProfileForVPP} onChange={e => setForm({ ...form, userProfileForVPP: e.target.value })}>
                        <option value="">Select VPP Profile</option>
                        {userVppOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Select if you want authorization for MPR</label>
                      <select className="form-select" value={form.userProfileForMPR} onChange={e => setForm({ ...form, userProfileForMPR: e.target.value })}>
                        <option value="">Select MPR Profile</option>
                        {userMprOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>
                  </>
                )}

                {/* Form Type "13": Application for creation of SSL VPN / Remote Access */}
                {form.selectRequestFromType === '13' && (
                  <>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Operating System Name and Version</label>
                      <input type="text" className="form-control" placeholder="Version 10" value={form.operatingSystemVersion} onChange={e => setForm({ ...form, operatingSystemVersion: e.target.value })} />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Antivirus (Name and Version)</label>
                      <input type="text" className="form-control" placeholder="Avast / Defender" value={form.antivirus} onChange={e => setForm({ ...form, antivirus: e.target.value })} />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Firewall status</label>
                      <select className="form-select" value={form.firewallStatus} onChange={e => setForm({ ...form, firewallStatus: e.target.value })}>
                        <option value="">Select Firewall Status</option>
                        {firewallStatusOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">Valid From</label>
                      <input type="date" className="form-control" value={form.accessDurationFrom} onChange={e => setForm({ ...form, accessDurationFrom: e.target.value })} />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">To</label>
                      <input type="date" className="form-control" value={form.accessDurationTo} onChange={e => setForm({ ...form, accessDurationTo: e.target.value })} />
                    </div>
                    <div className="col-md-4 mt-3">
                      <label className="form-label text-dark fw-semibold small">System MAC address</label>
                      <input type="text" className="form-control" placeholder="10.16.173.81" value={form.macAddress} onChange={e => setForm({ ...form, macAddress: e.target.value })} />
                    </div>
                    <div className="col-12 mt-3">
                      <label className="form-label text-dark fw-semibold small">Details of server you need to access</label>
                      <input type="text" className="form-control" placeholder="SSL VPN / Server Info" value={form.detailsOfServer} onChange={e => setForm({ ...form, detailsOfServer: e.target.value })} />
                    </div>
                  </>
                )}

                {/* Form Type "14": Application for Third-Party Email / Website Access Request */}
                {form.selectRequestFromType === '14' && (
                  <>
                    <div className="col-md-6 mt-3">
                      <label className="form-label text-dark fw-semibold small">Select E-mail Service</label>
                      <select className="form-select" value={form.services} onChange={e => setForm({ ...form, services: e.target.value })}>
                        <option value="">Select Service</option>
                        {servicesOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6 mt-3">
                      <label className="form-label text-dark fw-semibold small">System IP Address</label>
                      <input type="text" className="form-control" placeholder="e.g. 10.10.20.30" value={form.systemIPAddress} onChange={e => setForm({ ...form, systemIPAddress: e.target.value })} />
                    </div>
                    <div className="col-12 mt-3">
                      <label className="form-label text-dark fw-semibold small">Any other remarks</label>
                      <textarea className="form-control" rows={2} placeholder="Access justification" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })}></textarea>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>Cancel</button>
                <button type="button" className="btn btn-outline-primary" onClick={() => handleFormSubmit('V')} disabled={isSubmitting}>Save Draft</button>
                <button type="button" className="btn btn-danger" onClick={() => handleFormSubmit('C')} disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Requisition'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Dynamic Form Type Selection Modal */}
        {showTypeModal && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-white border-bottom py-2 px-3">
                  <h6 className="modal-title text-dark fw-bold mb-0 d-flex align-items-center fs-15">
                    <i className="ti ti-list-check text-danger me-2 fs-18"></i>Select Request Form Type
                  </h6>
                  <button type="button" className="btn-close" onClick={() => setShowTypeModal(false)}></button>
                </div>
                <div className="modal-body p-3">
                  <div className="list-group gap-2">
                    {formTypesList.map(t => (
                      <button
                        key={t.code}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between py-2 px-3 rounded border ${form.selectRequestFromType === t.code ? 'active bg-danger text-white border-danger' : ''}`}
                        onClick={() => { setForm({ ...form, selectRequestFromType: t.code }); setShowTypeModal(false); }}
                      >
                        <span className="fw-medium fs-14">{t.code} - {t.label}</span>
                        <i className={`ti ti-chevron-right fs-16 ${form.selectRequestFromType === t.code ? 'text-white' : 'text-muted'}`}></i>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Equipment Item Modal */}
        {showItemModal && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-white border-bottom py-2 px-3">
                  <h6 className="modal-title fw-bold text-dark mb-0 d-flex align-items-center fs-15">
                    <i className="ti ti-devices text-danger me-2 fs-18"></i>Add Equipment Item
                  </h6>
                  <button type="button" className="btn-close" onClick={() => setShowItemModal(false)}></button>
                </div>
                <div className="modal-body p-3">
                  <div className="mb-2">
                    <label className="form-label fw-semibold text-dark small mb-1">Equipment Item <span className="text-danger">*</span></label>
                    <select className="form-select form-select-sm" value={newItem.Items} onChange={e => setNewItem({ ...newItem, Items: e.target.value })}>
                      {itemOptionsList.map(o => <option key={o.code} value={o.code}>{o.code} - {o.label}</option>)}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label fw-semibold text-dark small mb-1">Allotment Requirement <span className="text-danger">*</span></label>
                    <select className="form-select form-select-sm" value={newItem.AllotementOrNot} onChange={e => setNewItem({ ...newItem, AllotementOrNot: e.target.value })}>
                      <option value="1">Yes (Allot Equipment)</option>
                      <option value="0">No (Verification Only)</option>
                    </select>
                  </div>
                  <div className="mb-1">
                    <label className="form-label fw-semibold text-dark small mb-1">Remarks / Specification</label>
                    <textarea className="form-control form-control-sm" rows={2} placeholder="Specify model, capacity, or details..." value={newItem.Remarks} onChange={e => setNewItem({ ...newItem, Remarks: e.target.value })} />
                  </div>
                </div>
                <div className="modal-footer bg-light-subtle border-top py-2 px-3 d-flex align-items-center justify-content-end gap-2">
                  <button type="button" className="btn btn-light btn-sm px-3" onClick={() => setShowItemModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger btn-sm px-3 shadow-xs" onClick={handleSaveItemModal}>
                    <i className="ti ti-plus me-1"></i> Add Item to List
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERequisitionITCreate;
